import { useCallback, useEffect, useRef } from 'react';
import { buildGovBrLogin, readGovBrSessionMessage, takeGovBrSessionId } from './govBrLogin';
import { AUTH_TIMEOUT_MS, GOVBR_POPUP_FEATURES, GOVBR_POPUP_NAME, SESSION_POLL_INTERVAL_MS } from './constants';
import { SncrErrorCode } from './types';

interface UseGovBrPopupAuthCallbacks {
  /** Recebe o session id do callback, ou `undefined` quando a sessão já foi confirmada pelo backend. */
  onAuthenticated(sessionId?: string): void;
  onError(code: SncrErrorCode): void;
  /** Consulta no backend se o prescritor já tem sessão Gov.br ativa. */
  isAuthenticated(): Promise<boolean>;
}

type AuthResult = { sessionId?: string } | { code: SncrErrorCode };

/**
 * Controla o login Gov.br via popup e o retorno do session id para a modal.
 *
 * O session id chega por `postMessage` do callback ou, como fallback, pelo
 * `localStorage`. Em iframe cross-site (modal dentro do B2C) o `localStorage`
 * é particionado e o fallback não funciona; se mesmo assim o popup fechar sem
 * session id, o backend é consultado antes de acusar `login_abandoned`, pois a
 * sessão na ANVISA pode já ter sido criada pelo login.
 *
 * @param callbacks - Handlers de sucesso, erro e checagem de sessão no backend.
 * @returns `openFromUserGesture` (deve ser chamado num clique, senão o popup é bloqueado) e `cancel`.
 */
export function useGovBrPopupAuth(callbacks: UseGovBrPopupAuthCallbacks) {
  const callbacksRef = useRef(callbacks);
  callbacksRef.current = callbacks;

  const popupRef = useRef<Window | null>(null);
  const timerIdsRef = useRef<number[]>([]);
  const messageListenerRef = useRef<((event: MessageEvent) => void) | null>(null);
  const isActiveRef = useRef(false);

  const stopWatching = useCallback(() => {
    timerIdsRef.current.forEach((id) => {
      window.clearInterval(id);
      window.clearTimeout(id);
    });
    timerIdsRef.current = [];

    if (messageListenerRef.current) {
      window.removeEventListener('message', messageListenerRef.current);
      messageListenerRef.current = null;
    }
  }, []);

  const stop = useCallback(
    (closePopup: boolean) => {
      isActiveRef.current = false;
      stopWatching();

      if (closePopup && popupRef.current && !popupRef.current.closed) {
        popupRef.current.close();
      }
      popupRef.current = null;
    },
    [stopWatching]
  );

  const finish = useCallback(
    (result: AuthResult) => {
      if (!isActiveRef.current) return;
      if ('code' in result) {
        stop(true);
        callbacksRef.current.onError(result.code);
      } else {
        // Não fecha o popup aqui: ele mostra o resultado e se autofecha após o delay.
        stop(false);
        callbacksRef.current.onAuthenticated(result.sessionId);
      }
    },
    [stop]
  );

  const finishAfterPopupClosed = useCallback(async () => {
    stopWatching();
    const authenticated = await callbacksRef.current.isAuthenticated().catch(() => false);
    finish(authenticated ? {} : { code: 'login_abandoned' });
  }, [stopWatching, finish]);

  const watchForSession = useCallback(
    (state: string) => {
      const onMessage = (event: MessageEvent) => {
        if (event.source !== popupRef.current || event.origin !== window.location.origin) return;
        const sessionId = readGovBrSessionMessage(event.data, state);
        if (sessionId) finish({ sessionId });
      };
      messageListenerRef.current = onMessage;
      window.addEventListener('message', onMessage);

      timerIdsRef.current.push(
        window.setInterval(() => {
          const sessionId = takeGovBrSessionId(state);
          if (sessionId) {
            finish({ sessionId });
            return;
          }
          if (popupRef.current?.closed) {
            finishAfterPopupClosed();
          }
        }, SESSION_POLL_INTERVAL_MS)
      );

      timerIdsRef.current.push(window.setTimeout(() => finish({ code: 'auth_expired' }), AUTH_TIMEOUT_MS));
    },
    [finish, finishAfterPopupClosed]
  );

  const openFromUserGesture = useCallback(() => {
    if (isActiveRef.current) return;

    const { state, loginUrl } = buildGovBrLogin();

    const popup = window.open(loginUrl, GOVBR_POPUP_NAME, GOVBR_POPUP_FEATURES);
    if (!popup) {
      callbacksRef.current.onError('popup_blocked');
      return;
    }

    popupRef.current = popup;
    isActiveRef.current = true;
    watchForSession(state);
  }, [watchForSession]);

  const cancel = useCallback(() => stop(true), [stop]);

  useEffect(() => {
    return () => stop(true);
  }, [stop]);

  return { openFromUserGesture, cancel };
}
